import React, { useEffect, useState } from "react";
import api from "../lib/api";
import Layout from "../components/Layout";
import { PageHeader, Btn, Field, Input, Select, StatusBadge, EmptyState } from "../components/UI";
import { Download } from "lucide-react";

export default function Reports() {
  const [type, setType] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [items, setItems] = useState([]);
  const [agentRows, setAgentRows] = useState([]);

  const load = async () => {
    const q = new URLSearchParams({report_type: type});
    if (start) q.append("start_date", start);
    if (end) q.append("end_date", end);
    const r = await api.get(`/reports-consignments?${q}`);
    setItems(r.data);
    const a = await api.get(`/reports-agents`);
    setAgentRows(a.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportCSV = () => {
    const headers = ["LR Number","Date","Shipper","Consignee","Origin","Destination","Boxes","Weight","Status","Total"];
    const rows = items.map(i => [i.lr_number, i.lr_date, i.shipper_name, i.consignee_name, i.origin_branch_name, i.destination_branch_name, i.boxes, i.weight, i.status, i.total_amount]);
    const csv = [headers, ...rows].map(r => r.map(v => `"${(v ?? "").toString().replace(/"/g,'""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], {type: "text/csv"});
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `report-${type}-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(url);
  };

  const printPDF = () => window.print();

  const revenue = items.reduce((s,i)=> s + (i.total_amount || 0), 0);

  return (
    <Layout>
      <PageHeader title="Reports" subtitle="Generate operational & financial reports"
        actions={<>
          <Btn variant="ghost" onClick={printPDF}><Download size={15} className="inline -mt-0.5 mr-1"/>PDF</Btn>
          <Btn onClick={exportCSV} data-testid="report-export"><Download size={15} className="inline -mt-0.5 mr-1"/>Export Excel</Btn>
        </>}/>

      <div className="lt-card p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-3">
        <Field label="Report Type">
          <Select value={type} onChange={e=>setType(e.target.value)} data-testid="report-type">
            <option value="all">All Shipments</option>
            <option value="delivered">Delivered</option>
            <option value="pending">Pending</option>
          </Select>
        </Field>
        <Field label="From"><Input type="date" value={start} onChange={e=>setStart(e.target.value)}/></Field>
        <Field label="To"><Input type="date" value={end} onChange={e=>setEnd(e.target.value)}/></Field>
        <div className="flex items-end"><Btn onClick={load} data-testid="report-run">Run Report</Btn></div>
        <div className="flex items-end justify-end text-right">
          <div><div className="text-xs text-slate-500">Revenue (in report)</div><div className="font-bold text-xl">₹{revenue.toLocaleString("en-IN")}</div></div>
        </div>
      </div>

      {items.length === 0 ? <EmptyState title="No data for selected filter"/> : (
        <div className="lt-card overflow-x-auto mb-6">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr><th className="text-left px-4 py-2.5">LR</th><th className="text-left">Date</th><th className="text-left">Route</th><th className="text-left">Shipper</th><th className="text-left">Consignee</th><th className="text-left">Status</th><th className="text-right px-4">Amount</th></tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr key={i.id} className="border-t">
                  <td className="px-4 py-2 font-semibold">{i.lr_number}</td>
                  <td>{i.lr_date}</td>
                  <td>{i.origin_branch_name} → {i.destination_branch_name}</td>
                  <td>{i.shipper_name}</td>
                  <td>{i.consignee_name}</td>
                  <td><StatusBadge status={i.status}/></td>
                  <td className="text-right px-4">₹{(i.total_amount||0).toLocaleString("en-IN")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="lt-card overflow-hidden">
        <div className="px-4 py-3 border-b font-semibold">Agent Performance</div>
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr><th className="text-left px-4 py-2.5">Agent</th><th className="text-left">Shipments</th><th className="text-left">Delivered</th><th className="text-right px-4">Revenue</th></tr>
          </thead>
          <tbody>
            {agentRows.map(a => (
              <tr key={a._id||"unassigned"} className="border-t">
                <td className="px-4 py-2 font-medium">{a.agent_name}</td>
                <td>{a.shipments}</td>
                <td>{a.delivered}</td>
                <td className="text-right px-4">₹{(a.revenue||0).toLocaleString("en-IN")}</td>
              </tr>
            ))}
            {agentRows.length===0 && <tr><td colSpan={4} className="text-center py-4 text-slate-400">No agents data</td></tr>}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
